import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Slide, { SlideProps } from '@mui/material/Slide';

type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

interface SnackbarMessage {
  id: string;
  title?: string;
  message: string;
  severity: SnackbarSeverity;
  duration?: number;
}

interface SnackbarContextType {
  showSnackbar: (options: {
    title?: string;
    message: string;
    severity?: SnackbarSeverity;
    duration?: number;
  }) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [snackbars, setSnackbars] = useState<SnackbarMessage[]>([]);

  const showSnackbar = useCallback(({
    title,
    message,
    severity = 'info',
    duration = 5000,
  }: {
    title?: string;
    message: string;
    severity?: SnackbarSeverity;
    duration?: number;
  }) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSnackbars((prev) => [...prev, { id, title, message, severity, duration }]);
  }, []);

  const showSuccess = useCallback((message: string, title?: string) => {
    showSnackbar({ message, title, severity: 'success' });
  }, [showSnackbar]);

  const showError = useCallback((message: string, title?: string) => {
    showSnackbar({ message, title, severity: 'error' });
  }, [showSnackbar]);

  const showWarning = useCallback((message: string, title?: string) => {
    showSnackbar({ message, title, severity: 'warning' });
  }, [showSnackbar]);

  const showInfo = useCallback((message: string, title?: string) => {
    showSnackbar({ message, title, severity: 'info' });
  }, [showSnackbar]);

  const handleClose = useCallback((id: string) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  }, []);

  return (
    <SnackbarContext.Provider
      value={{ showSnackbar, showSuccess, showError, showWarning, showInfo }}
    >
      {children}
      {snackbars.map((snackbar, index) => (
        <Snackbar
          key={snackbar.id}
          open
          autoHideDuration={snackbar.duration}
          onClose={() => handleClose(snackbar.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={SlideTransition}
          sx={{ bottom: { xs: 90 + index * 80, sm: 24 + index * 80 } }}
        >
          <Alert
            onClose={() => handleClose(snackbar.id)}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%', minWidth: 300 }}
          >
            {snackbar.title && <AlertTitle>{snackbar.title}</AlertTitle>}
            {snackbar.message}
          </Alert>
        </Snackbar>
      ))}
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}

// Compatibility hook for existing useToast usage
export function useToast() {
  const { showSnackbar, showSuccess, showError } = useSnackbar();

  const toast = useCallback(({
    title,
    description,
    variant = 'default',
  }: {
    title?: string;
    description: string;
    variant?: 'default' | 'destructive' | 'success';
  }) => {
    const severity = variant === 'destructive' ? 'error' : variant === 'success' ? 'success' : 'info';
    showSnackbar({ title, message: description, severity });
  }, [showSnackbar]);

  return { toast, showSuccess, showError };
}
